import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { FREE_PRODUCT_LIMIT, GALLERY_PRO_ONLY } from '../plans';
import { productPhotos } from '../gallery.js';
import Carousel from '../components/menu/Carousel.jsx';
import PromoBanner, { useLivePromos } from '../components/menu/PromoBanner.jsx';
import { formatPrice, menuUrl, productTags, takesOrders } from '../menu.js';
import { QR_PARAM, QR_VALUE, trackAddToCart, trackProductView, trackVisit, trackWhatsappClick } from '../stats.js';
import { themeOf, themeStyle } from '../menuTheme.js';
import { MenuHeader, MenuItem, MenuReviews, MenuSkeleton, ScrollTop } from '../components/menu/MenuParts.jsx';
import { CartBar, CartContent, PaymentReturn, QtyStepper, useCart } from '../components/menu/Cart.jsx';
import Dialog from '../components/panel/Dialog';
import Icon from '../components/panel/Icon';
import Seo from '../components/Seo';
import { detectLang, saveLang, uiTexts } from '../i18n.js';
import { useMenuTranslation } from '../menuTranslation.js';
import '../menu.css';

const ALL = "Todo";

// Título, descripción y datos estructurados del negocio para Google y las vistas previas.
const businessSeo = (business, products) => {
    const name = business.nombreEmpresa || business.usuarioEmpresa;
    const place = [...new Set([business.ciudad, business.provincia].filter(Boolean))].join(", ");
    const sample = products.slice(0, 4).map((p) => p.name).filter(Boolean).join(", ");
    const description = (business.descripcionEmpresa ? business.descripcionEmpresa.trim().replace(/\.?$/, ". ") : "")
        + `Mirá el menú de ${name}${place ? " en " + place : ""} con fotos y precios actualizados`
        + (sample ? `: ${sample} y más.` : ".");
    const address = [business.calle, business.altura].filter(Boolean).join(" ");

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": menuUrl(business.usuarioEmpresa) + "#negocio",
        name,
        url: menuUrl(business.usuarioEmpresa),
        ...(business.descripcionEmpresa && { description: business.descripcionEmpresa }),
        ...(business.logo && { image: business.logo, logo: business.logo }),
        ...(business.telefono && { telephone: business.telefono }),
        ...((address || place) && {
            address: {
                "@type": "PostalAddress",
                ...(address && { streetAddress: address }),
                ...(business.ciudad && { addressLocality: business.ciudad }),
                ...(business.provincia && { addressRegion: business.provincia }),
                addressCountry: "AR"
            }
        })
    };

    return {
        title: `${name}${business.ciudad ? " - " + business.ciudad : ""} | Menú y precios`,
        description: description.length > 300 ? description.slice(0, 297) + "…" : description,
        jsonLd
    };
};

// Fotos del producto en el detalle: una sola, o varias para pasar con el dedo (Pro).
const ProductGallery = ({ photos, name, lang }) => {
    if (!photos.length) return null;
    if (photos.length === 1) return <img className="lp-menu-detail-photo" src={photos[0]} alt={name}/>;
    return (
        <Carousel count={photos.length} label={uiTexts(lang).photos(name)} className="lp-menu-gallery" lang={lang}>
            {photos.map((url, i) => <img key={url} className="lp-menu-detail-photo" src={url} alt={i === 0 ? name : `${name} (${i + 1})`} loading={i ? "lazy" : undefined} draggable="false"/>)}
        </Carousel>
    );
};

// Menú público de cada negocio: /:usuarioEmpresa.
const Business = () => {

    const { usuarioEmpresa } = useParams();

    const [business, setBusiness] = useState();
    const [products, setProducts] = useState();
    const [failed, setFailed] = useState(false);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState(ALL);
    const [view, setView] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    // Número del pedido al volver de pagar con Mercado Pago (?pago=N).
    const [payReturn, setPayReturn] = useState(() => {
        const numero = Number(new URLSearchParams(window.location.search).get("pago"));
        return Number.isInteger(numero) && numero > 0 ? numero : null;
    });
    const [lang, setLang] = useState(detectLang);
    const { tr, tag, status: translation } = useMenuTranslation(business, products, lang);
    const t = uiTexts(lang);
    const cart = useCart(usuarioEmpresa, products, (id) => business && trackAddToCart(business, id));
    const promos = useLivePromos(business);

    // Mercado Pago vuelve con ?pago=N y sus propios datos del pago: se sacan de la dirección
    // para que, si el cliente recarga o comparte el link, no vuelva a ver el pago.
    useEffect(() => {
        if (!payReturn) return;
        setCartOpen(true);
        window.history.replaceState(window.history.state, "", window.location.pathname + window.location.hash);
    }, [payReturn]);

    useEffect(() => {
        if (usuarioEmpresa === "not-found") return;

        const load = async () => {
            try {
                // menus/{usuarioEmpresa} es la copia pública del negocio, sin el email del dueño.
                const found = await getDoc(doc(db, "menus", usuarioEmpresa));
                if (!found.exists()) {
                    window.location.href = "/not-found";
                    return;
                }

                const data = found.data();
                const productDocs = await getDocs(query(collection(db, "products"), where("userID", "==", data.id)));
                const list = productDocs.docs.map((d) => d.data());

                setBusiness(data);
                // Los QR abren el menú con ?o=qr: se cuenta de dónde vino y se saca de la
                // dirección para que, si el cliente comparte el link, no cuente como QR.
                const params = new URLSearchParams(window.location.search);
                const fromQr = params.get(QR_PARAM) === QR_VALUE;
                if (fromQr) {
                    params.delete(QR_PARAM);
                    const rest = params.toString();
                    window.history.replaceState(window.history.state, "", window.location.pathname + (rest ? "?" + rest : "") + window.location.hash);
                }
                trackVisit(data, fromQr);
                // En el plan Gratis el menú muestra hasta FREE_PRODUCT_LIMIT productos.
                setProducts(data.subscribed === true ? list : list.slice(0, FREE_PRODUCT_LIMIT));
            } catch (error) {
                console.error(error);
                setFailed(true);
            }
        }

        load();
    }, [usuarioEmpresa]);

    // El fondo del body se ve al estirar la página en el celular, y la barra de scroll
    // es la de la página, así que las dos toman los colores del negocio desde acá.
    const { bodyColor, buttonColor, arrowColor } = themeOf(business);
    useEffect(() => {
        const html = document.documentElement.style;
        const body = document.body.style;
        body.backgroundColor = bodyColor;
        // Chrome, Edge y Firefox.
        html.scrollbarColor = buttonColor + " " + bodyColor;
        // Safari (body::-webkit-scrollbar en index.css).
        body.setProperty("--scrollBg", bodyColor);
        body.setProperty("--scrollThumb", buttonColor);
        body.setProperty("--scrollThumbHover", arrowColor);
        return () => {
            body.backgroundColor = "";
            html.scrollbarColor = "";
            ["--scrollBg", "--scrollThumb", "--scrollThumbHover"].forEach((name) => body.removeProperty(name));
        };
    }, [bodyColor, buttonColor, arrowColor]);

    // El idioma elegido queda en la página (lectores de pantalla, traductor del navegador).
    useEffect(() => {
        document.documentElement.lang = lang;
        return () => { document.documentElement.lang = "es"; };
    }, [lang]);
    const changeLang = (code) => {
        setLang(code);
        saveLang(code);
    };

    const categories = useMemo(() => {
        if (!business || !products) return [];
        const own = Array.isArray(business.categories) ? business.categories : [];
        // Productos de una categoría que ya no está en la lista van al final.
        const orphans = [...new Set(products.map((p) => p.category).filter((c) => c && !own.includes(c)))];
        return [...own, ...orphans].filter((c) => products.some((p) => p.category === c));
    }, [business, products]);

    if (failed) {
        return (
            <div className="lp-menu lp-menu-center" style={themeStyle(themeOf())}>
                <p>{t.loadError}</p>
                <button type="button" className="lp-menu-btn lp-menu-btn-primary" onClick={() => window.location.reload()}>{t.retry}</button>
            </div>
        )
    }

    if (!business || !products) {
        return <div className="lp-menu" style={themeStyle(themeOf())}><MenuSkeleton lang={lang}></MenuSkeleton></div>
    }

    const theme = themeOf(business);
    const orders = takesOrders(business);
    const openProduct = (product) => {
        setView(product);
        trackProductView(business, product.id);
    };
    const term = q.trim().toLowerCase();
    // Busca en el texto que se ve y también en el original en español.
    const matches = (text) => !!text && (text.toLowerCase().includes(term) || tr(text).toLowerCase().includes(term));
    // Las etiquetas (plan Pro) también se buscan: "sin tacc" trae todo lo que tenga esa etiqueta.
    const matchesTag = (p) => productTags(business, p).some((label) => matches(label) || tag(label).toLowerCase().includes(term));
    // Tocar una etiqueta de un producto la escribe en el buscador y vuelve a la lista completa.
    const searchTag = (label) => {
        setView(null);
        setCat(ALL);
        setQ(label);
        const bar = document.querySelector(".lp-menu-bar");
        if (bar) window.scrollTo({ top: bar.getBoundingClientRect().top + window.scrollY, behavior: "smooth" });
    };
    const sections = categories
        .filter((c) => cat === ALL || c === cat)
        .map((c) => ({
            name: c,
            items: products
                .filter((p) => p.category === c && (!term || matches(p.name) || matches(p.description) || matchesTag(p)))
                .sort((a, b) => (tr(a.name) || "").localeCompare(tr(b.name) || "", lang))
        }))
        .filter((s) => s.items.length);

  return (
    <div className={orders ? "lp-menu lp-menu-orders" : "lp-menu"} style={themeStyle(theme)}>
        <Seo
            {...businessSeo(business, products)}
            path={"/" + (business.usuarioEmpresa || usuarioEmpresa)}
            image={business.logo || undefined}
        ></Seo>
        <Helmet>
            <meta name="theme-color" content={theme.bodyColor} />
        </Helmet>

        <div className="lp-menu-page">
            <MenuHeader business={business} onWhatsapp={() => trackWhatsappClick(business)} lang={lang} tr={tr} onLang={changeLang}></MenuHeader>

            <PromoBanner promos={promos} products={products} onOpen={openProduct} lang={lang} tr={tr}></PromoBanner>

            {products.length > 0 &&
                <div className="lp-menu-bar">
                    <div className="lp-menu-search">
                        <label htmlFor="buscar-menu" className="lp-menu-sr">{t.search}</label>
                        <Icon name="search" size={18}></Icon>
                        <input id="buscar-menu" type="search" placeholder={t.searchPlaceholder} value={q} onChange={(e) => setQ(e.target.value)}/>
                    </div>
                    {categories.length > 1 &&
                        <div className="lp-menu-chips" role="group" aria-label={t.categories}>
                            {[ALL, ...categories].map((c) => (
                                <button key={c} type="button" className={cat === c ? "lp-menu-chip lp-menu-chip-on" : "lp-menu-chip"} aria-pressed={cat === c} onClick={() => setCat(c)}>{c === ALL ? t.all : tr(c)}</button>
                            ))}
                        </div>
                    }
                </div>
            }

            {translation !== "ready" &&
                <p className={"lp-menu-translating" + (translation === "failed" ? " lp-menu-translating-failed" : "")} role="status">
                    {translation === "translating" && <span className="lp-menu-translating-dot" aria-hidden="true"></span>}
                    {translation === "translating" ? t.translating : t.translateFailed}
                </p>
            }

            <main className="lp-menu-main">
                {sections.map((s) => (
                    <section key={s.name} className="lp-menu-section">
                        <h2>{tr(s.name)}</h2>
                        <div className="lp-menu-items">
                            {s.items.map((p) => <MenuItem key={p.id} product={p} onOpen={openProduct} onTag={searchTag} cart={orders ? cart : null} tags={productTags(business, p).map((label) => [label, tag(label)])} lang={lang} tr={tr}></MenuItem>)}
                        </div>
                    </section>
                ))}

                {!sections.length &&
                    <p className="lp-menu-empty lp-menu-muted">
                        {products.length ? t.noResults(q) : t.emptyMenu}
                    </p>
                }

                <MenuReviews business={business} lang={lang}></MenuReviews>

                {!business.subscribed &&
                    <a className="lp-menu-made" href="/" target="_blank" rel="noreferrer">{t.madeWith[0]}<strong>MiQRapp</strong>{t.madeWith[1]}</a>
                }
            </main>
        </div>

        <ScrollTop raised={orders && cart.count > 0} lang={lang}></ScrollTop>
        {orders && <CartBar cart={cart} onOpen={() => setCartOpen(true)} lang={lang}></CartBar>}

        <Dialog open={!!view} title={tr(view?.name) || ""} onClose={() => setView(null)}>
            {view &&
                <div className="lp-menu-detail" style={themeStyle(theme)}>
                    <ProductGallery photos={productPhotos(business, view, GALLERY_PRO_ONLY)} name={tr(view.name)} lang={lang}></ProductGallery>
                    {productTags(business, view).length > 0 &&
                        <span className="lp-menu-item-tags">{productTags(business, view).map((label) => <button key={label} type="button" className="lp-menu-label" title={t.searchTag(tag(label))} onClick={() => searchTag(tag(label))}>{tag(label)}</button>)}</span>
                    }
                    {view.description && <p>{tr(view.description)}</p>}
                    <div className="lp-menu-detail-foot">
                        <strong>{formatPrice(view.price)}</strong>
                        {view.agotado && <span className="lp-menu-tag">{t.soldOutLong}</span>}
                        {orders && !view.agotado && (cart.qtyOf(view.id) ?
                            <QtyStepper className="lp-menu-detail-qty" lang={lang} name={tr(view.name)} qty={cart.qtyOf(view.id)} onAdd={() => cart.add(view.id)} onRemove={() => cart.remove(view.id)}></QtyStepper> :
                            <button type="button" className="lp-menu-btn lp-menu-btn-primary lp-menu-detail-qty" onClick={() => cart.add(view.id)}>{t.addToOrder}</button>
                        )}
                    </div>
                </div>
            }
        </Dialog>

        {(orders || payReturn) &&
            <Dialog open={cartOpen} title={t.yourOrder} onClose={() => { setCartOpen(false); setPayReturn(null); }} className="lp-menu-cart-dialog">
                <div className="lp-menu-cart-wrap" style={themeStyle(theme)}>
                    {payReturn ?
                        <PaymentReturn business={business} numero={payReturn} onClose={() => { setCartOpen(false); setPayReturn(null); }} lang={lang}></PaymentReturn> :
                        <CartContent business={business} cart={cart} onClose={() => setCartOpen(false)} lang={lang} tr={tr}></CartContent>
                    }
                </div>
            </Dialog>
        }
    </div>
  )
}

export default Business
